import { DefaultColorFactory } from './default-color-factory';
import { IChartConfig } from './chart-config.interface';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IChartDrawer } from './chart-drawer.interface';
import { IColorFactory } from './color-factory.interface';
import { OverflowedData } from './overflowed-data.interface';

export class OverflowedHorizontalBarChartDrawer implements IChartDrawer {
  private readonly _chartMax: number;
  private readonly _colorFactory: IColorFactory;
  private readonly _data: OverflowedData[][];
  private readonly _groupGap: number;
  private readonly _barHeight: number;
  private readonly _barGap: number;
  private readonly _ticksCount: number = 5;
  private readonly _paddingLeft = 150;
  private readonly _paddingRight = 50;
  private readonly _paddingBottom = 25;
  private readonly _barGapCoefficient = 0.25;
  private readonly _groupGapCoefficient = 1;
  private readonly _minChartHeight = 350;
  private readonly _heightOfOneGroup = 70;
  private readonly _tickStep: number;
  private readonly _tickLabel: string;
  private readonly _height: number;
  private readonly _width: number;
  private readonly _labels: string[];

  constructor(chartConfig: IChartConfig, private readonly domSanitizer: DomSanitizer) {
    this._data = chartConfig.overflowedData;

    this._colorFactory = chartConfig.colorFactory ?? new DefaultColorFactory();

    this._labels = chartConfig.labels;

    this._tickLabel = chartConfig.tickLabel;
    this._width = chartConfig.width;

    const totalBars = this._data.reduce((sum, x) => sum + x.length, 0);
    const totalGroups = this._data.length;

    this._height = Math.max(this._minChartHeight, this._heightOfOneGroup * totalGroups);

    this._barHeight =
      (this._height - this._paddingBottom) /
      (totalBars * (1 + this._barGapCoefficient) + totalGroups * (this._groupGapCoefficient - this._barGapCoefficient));

    this._groupGap = this._barHeight * this._groupGapCoefficient;

    this._barGap = this._barHeight * this._barGapCoefficient;

    const max = Math.max(
      chartConfig.alternativeMax ?? 0,
      Math.max(...this._data.map((x) => Math.max(...x.map((y) => Math.max(y.completed, y.required)))))
    );

    this._tickStep = max === 0 ? 1 : Math.ceil(max / 5);

    if (chartConfig.tickStep) {
      this._tickStep = chartConfig.tickStep;
      this._ticksCount = Math.ceil(max) / chartConfig.tickStep;
    }

    this._chartMax = this._tickStep * this._ticksCount;
  }

  public get(): SafeHtml {
    let sb = '';

    sb += `<div class="horizontal-bar-chart" style="width: ${this._width}px; padding: 0; margin-left: auto; margin-right: auto; position: relative">`;

    sb += `<svg viewBox="0 0 ${this._width} ${this._height}" width="${this._width}px" height="${this._height}px">`;

    sb += this.getAxes();

    sb += this.getBars();

    sb += '</svg>';

    if (this._labels.length > 0) {
      sb += this.getLabels();
    }

    sb += '</div>';

    return this.domSanitizer.bypassSecurityTrustHtml(sb);
  }

  public getIsOverflowed(): boolean {
    return false;
  }

  public getMinOffset(): number {
    return 0;
  }

  private getAxes(): string {
    let sb = '';

    sb += '<g>';
    sb += `<path d="M ${this._paddingLeft} 0 V ${
      this._height - this._paddingBottom
    }" fill="none" stroke="#b1b1b1"></path>`;
    sb += `<text text-anchor="middle" dy="1em" transform="translate(${this._paddingLeft} ${
      this._height - this._paddingBottom
    })">0 ${this._tickLabel}</text>`;

    for (let i = 1; i <= this._ticksCount; i++) {
      sb += `<path d="M ${
        this._paddingLeft + ((this._width - this._paddingLeft - this._paddingRight) / this._ticksCount) * i
      } 0 V ${this._height - this._paddingBottom}" fill="none" stroke-dasharray="2" stroke="#b1b1b1"></path>`;
      sb += `<text text-anchor="middle" dy="1em" transform="translate(${
        this._paddingLeft + ((this._width - this._paddingLeft - this._paddingRight) / this._ticksCount) * i
      } ${this._height - this._paddingBottom})">${this._tickStep * i} ${this._tickLabel}</text>`;
    }

    sb += '</g>';

    return sb;
  }

  private getBars(): string {
    let sb = '';

    sb += '<g>';

    let groupIndex = 0;
    let barIndex = 0;

    for (const group of this._data) {
      sb += '<g>';

      let barIndexInGroup = 0;

      for (const { completed, required } of group) {
        if (completed <= required) {
          if (completed > 0) {
            sb += `<path d="${this.getBarPath(completed, barIndex, groupIndex)}" class="${this._colorFactory.get(
              barIndex,
              barIndexInGroup,
              completed
            )}" fill="#9B9B9B"></path>`;
          }

          sb += `<path d="${this.getOverBarPath(required, completed, barIndex, groupIndex)}" fill="#ECECEC"></path>`;
        } else {
          if (required > 0) {
            sb += `<path d="${this.getBarPath(required, barIndex, groupIndex)}" class="${this._colorFactory.get(
              barIndex,
              barIndexInGroup,
              required
            )}" fill="#9B9B9B"></path>`;
          }

          sb += `<path d="${this.getOverBarPath(completed, required, barIndex, groupIndex)}" fill="#D0021B"></path>`;
        }

        barIndex++;
        barIndexInGroup++;
      }

      sb += '</g>';

      groupIndex++;
    }

    sb += '</g>';

    return sb;
  }

  private getBarPath(rawValue: number, barIndex: number, groupIndex: number): string {
    const value = Math.max(rawValue / this._chartMax, 0.01);

    let sb = '';

    const x1 = this._paddingLeft;
    const y1 =
      this._groupGap / 2 +
      this._groupGap * groupIndex +
      this._barHeight * barIndex +
      this._barGap * (barIndex - groupIndex);

    sb += `M ${x1} ${y1}`;

    const x2 = (this._width - this._paddingLeft - this._paddingRight) * value + this._paddingLeft;
    const y2 = y1;

    sb += `H ${x2}`;

    const y3 = y2 + this._barHeight;

    sb += `V ${y3}`;

    const x4 = x1;

    sb += `H ${x4}`;

    sb += 'Z';

    return sb;
  }

  private getOverBarPath(
    rawValueCompleted: number,
    rawValueRequired: number,
    barIndex: number,
    groupIndex: number
  ): string {
    const completedValue = Math.max(rawValueCompleted / this._chartMax, 0.01);
    const requiredValue = rawValueRequired > 0 ? Math.max(rawValueRequired / this._chartMax, 0.01) : 0;

    let sb = '';

    const x1 = (this._width - this._paddingLeft - this._paddingRight) * requiredValue + this._paddingLeft;
    const y1 =
      this._groupGap / 2 +
      this._groupGap * groupIndex +
      this._barHeight * barIndex +
      this._barGap * (barIndex - groupIndex);

    sb += `M ${x1} ${y1}`;

    const x2 = (this._width - this._paddingLeft - this._paddingRight) * completedValue + this._paddingLeft;
    const y2 = y1;

    sb += `H ${x2}`;

    const y3 = y2 + this._barHeight;

    sb += `V ${y3}`;

    const x4 = x1;

    sb += `H ${x4}`;

    sb += 'Z';

    return sb;
  }

  private getLabels(): string {
    let sb = '';

    sb += `<div style="position: absolute; top: 0; left: 0; width: ${this._paddingLeft}px">`;

    const height = (this._height - this._paddingBottom) / this._labels.length;

    for (const label of this._labels) {
      sb += `<div style="height: ${height}px; line-height: ${height}px; text-align: right; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-right: 25px">${label}</div>`;
    }

    sb += '</div>';

    return sb;
  }
}
